/******************************************************************************

Copyright (c) 2016-2018, Highsoft

Permission is hereby granted, free of charge, to any person obtaining
a copy of this software and associated documentation files (the
"Software"), to deal in the Software without restriction, including
without limitation the rights to use, copy, modify, merge, publish,
distribute, sublicense, and/or sell copies of the Software, and to
permit persons to whom the Software is furnished to do so, subject to
the following conditions:

The above copyright notice and this permission notice shall be
included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND,
EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF
MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT.
IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY
CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT,
TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE
SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

******************************************************************************/

// @format
import { merge, isBasic, isFn } from './highcharts-editor';
import templates from './../meta/highed.meta.templates';

let mostPopularTemplates = [],
  newTemplates = {};

/** Install a new template
 *
 *  @example
 *  highed.installTemplate('custom', {
 *      title: 'My company template',
 *      tooltipText: 'Company template: requires no particular data',
 *      config: {
 *           'chart--title': 'Company Chart'
 *      }
 *  });
 *
 *  @param type {string} - template type: `line area column bar scatter pie polar stock`
 *  @param def {object} - the template definition
 *    > title {string} - the template title
 *    > config {object} - the highcharts attributes
 *    > tooltipText {string} - the tooltip text
 */
export const add = (type, def) => {
  let properties = merge(
    {
      title: '',
      description: '',
      constructor: '',
      thumbnail: '',
      icon: '',
      sampleSets: [],
      validator: false,
      config: {}
    },
    def
  );

  if (!isBasic(type)) {
    return false;
  }

  templates[type] = templates[type] || {
    description: '',
    icon: '',
    sampleData: [],
    templates: {}
  };

  if (properties.title.length) {
    if (properties.basic && !properties.isMap) {
      properties.parent = type;
      mostPopularTemplates.push(properties);
    }

    if (properties.isNew) {
      properties.parent = type;
      newTemplates[properties.title] = properties;
    }

    if (properties.icon) templates[type].icon = properties.icon;
    templates[type].templates[properties.title] = properties;
    //highed.log(4, '[templateman] - added template', properties.title);
    return true;
  }

  return false;
};

/**
 * Do something for each template
 * @param fn {function} - the callback
 */
export const each = (fn) => {
  if (isFn(fn)) {
    Object.keys(templates).forEach(function (cat) {
      Object.keys(templates[cat].templates).forEach(function (id) {
        fn(cat, templates[cat].templates[id]);
      });
    });
  }
};

/**
 * Do something for each template within a given category
 * @param cat {string} - the category to filter by
 * @param fn {function} - the callback
 */
export const eachInCategory = (cat, fn) => {
  if (isFn(fn) && templates[cat]) {
    Object.keys(templates[cat].templates).forEach(function (id) {
      fn(templates[cat].templates[id]);
    });
  }
};

/**
 * Get a reference to the templates within a given category
 */
export const getAllInCat = (cat) => {
  return templates[cat] ? templates[cat].templates : false;
};

/**
 * Get a reference to the templates within a given category, converted to an array
 */
export const getAllInCatToArray = (cat) => {
  if (templates[cat]) {
    return Object.keys(templates[cat].templates).map((key) => {
      return templates[cat].templates[key];
    });
  }
  return false;
};

/**
 * Get category meta
 */
export const getCatInfo = (cat) => {
  return merge(
    {
      id: cat
    },
    templates[cat] || {}
  );
};

export const getMostPopular = () => {
  if (mostPopularTemplates.length === 0) {
    const keys = Object.keys(templates);
    keys.forEach((key) => {
      const templateKeys = Object.keys(templates[key].templates);
      templateKeys.forEach((templateKey) => {
        const template = templates[key].templates[templateKey];
        if (template.basic && !template.isMap) {
          mostPopularTemplates.push(template);
        }
      });
    });
  }

  return mostPopularTemplates;
};

export const getNewTemplates = () => {
  return newTemplates;
};

/**
 * Get a list of id/title pairs for templates
 */
export const getCatArray = () => {
  return Object.keys(templates).map(function (cat) {
    return {
      id: cat,
      title: cat,
      icon: templates[cat].icon
    };
  });
};

/**
 * Get a list of id/title pairs for selected template
 */
export const getCatObj = (cat) => {
  return [
    {
      id: cat,
      title: cat,
      icon: templates[cat].icon
    }
  ];
};

/**
 * Add meta information to a category
 */
export const addCategory = (id, meta) => {
  templates[id] = templates[id] || {
    templates: {}
  };

  merge(templates[id], meta, false, { templates: 1 });
};

/**
 * Do something with each category
 * @param fn {function} - the callback
 */
export const eachCategory = (fn) => {
  if (isFn(fn)) {
    Object.keys(templates).forEach(function (id) {
      fn(id, templates[id]);
    });
  }
};

/** Flush templates */
export const flush = () => {
  templates = {};
};
/** Flush templates */
export const flushMostPopular = () => {
  mostPopularTemplates = [];
};

/** Add a new template type
 *  If the type already exists, nothing will happen
 *
 *  @example
 *  highed.addTemplateType('custom', 'My company templates');
 *
 *  @param type {string} - the type id
 *  @param title {string} - the title as it appears in the category list
 */
export const addType = (type, title) => {
  if (typeof templates === 'undefined') {
    templates = {};
  }

  if (typeof templates[type] === 'undefined') {
    templates[type] = {
      title: title,
      templates: {}
    };
  }
};

export const getTemplates = () => templates;
